import { ReactNode } from 'react';

import bumperMarkWhite from 'images/bumperMcp/bumper-mark-white.png';
import bumperMark from 'images/bumperMcp/bumper-mark.png';
import claudeLogo from 'images/bumperMcp/claude-logo.png';
import {
  CLAUDE_CONNECTORS_URL,
  MCP_OAUTH_CLIENT_ID,
  MCP_SERVER_URL,
} from 'routes/Mcp/constants';

import {
  AddConnectorButton,
  AddressBar,
  AssistantAvatar,
  AssistantRow,
  AssistantText,
  BrowserTab,
  ConnectorForm,
  ConnectorsBody,
  ConnectorsContent,
  ConnectorsNav,
  ConnectorsWindow,
  ContentTitle,
  FieldLabel,
  FieldValue,
  MockBrand,
  MockChat,
  MockSidebar,
  MockWindow,
  MutedLines,
  NavTitle,
  SidebarItem,
  StrongValue,
  TitleBar,
  UserBubble,
  UserRow,
  WindowDot,
  WindowDots,
} from './mockStyledComponents';
import { McpImage } from './styled';

const Img = ({ src, size }: { src: string; size: number }) => (
  <McpImage src={src} alt="" style={{ width: size, height: size }} />
);

interface MockItemProps {
  children: ReactNode;
  size: number;
  tone?: 'neutral' | 'accent';
  icon?: ReactNode;
}

const MockItem = ({ children, size, tone, icon }: MockItemProps) => (
  <SidebarItem size={size} tone={tone}>
    {icon}
    {children}
  </SidebarItem>
);

interface ClaudeChatMockProps {
  /** Smaller layout used inside the My Garage card. */
  compact?: boolean;
}

/** Claude app window showing a Bumper VIN lookup via the MCP connector. */
export const ClaudeChatMock = ({ compact = false }: ClaudeChatMockProps) => {
  const itemSize = compact ? 8 : 11;

  return (
    <MockWindow
      role="img"
      aria-label="Example Claude conversation using Bumper to check a VIN for accidents and market value"
    >
      <MockSidebar aria-hidden compact={compact}>
        <MockBrand compact={compact}>
          <Img src={claudeLogo} size={compact ? 10 : 16} />
          Claude
        </MockBrand>
        <MockItem size={itemSize} tone="neutral">
          New chat
        </MockItem>
        {!compact && <MockItem size={itemSize}>Chats</MockItem>}
        <MockItem size={itemSize}>Projects</MockItem>
        <MockItem
          size={itemSize}
          tone="accent"
          icon={<Img src={bumperMark} size={compact ? 10 : 11} />}
        >
          Connectors
        </MockItem>
      </MockSidebar>
      <MockChat aria-hidden compact={compact}>
        <UserRow>
          <UserBubble compact={compact}>
            {compact
              ? 'Check VIN for accidents & value.'
              : 'Check VIN 1HGCV1F34KA123456 for accidents and market value.'}
          </UserBubble>
        </UserRow>
        <AssistantRow compact={compact}>
          <AssistantAvatar compact={compact}>
            <Img src={bumperMarkWhite} size={compact ? 10 : 14} />
          </AssistantAvatar>
          <AssistantText compact={compact}>
            {compact ? (
              <>
                No accidents. Clean title. <strong>$21,400</strong> fair value.
              </>
            ) : (
              <>
                Here&apos;s what Bumper found for this 2019 Honda Accord EX-L:
                <MutedLines>
                  • No accidents reported
                  <br />• Clean title, single owner
                  <br />• Fair market value: <StrongValue>$21,400</StrongValue>
                </MutedLines>
              </>
            )}
          </AssistantText>
        </AssistantRow>
      </MockChat>
    </MockWindow>
  );
};

/** Browser window on claude.ai/customize with the "Add custom connector" form. */
export const ClaudeConnectorsMock = () => (
  <ConnectorsWindow
    role="img"
    aria-label={`Claude connectors page with a custom connector named Bumper, the URL ${MCP_SERVER_URL} and the OAuth client ID ${MCP_OAUTH_CLIENT_ID}`}
  >
    <TitleBar aria-hidden>
      <WindowDots>
        <WindowDot color="#FF5F57" />
        <WindowDot color="#FEBC2E" />
        <WindowDot color="#28C840" />
      </WindowDots>
      <BrowserTab>
        <Img src={claudeLogo} size={12} />
        Claude
      </BrowserTab>
    </TitleBar>
    <AddressBar aria-hidden>
      {CLAUDE_CONNECTORS_URL.replace('https://', '')}
    </AddressBar>
    <ConnectorsBody aria-hidden>
      <ConnectorsNav>
        <NavTitle>← Customize</NavTitle>
        <MockItem size={11}>Skills</MockItem>
        <MockItem size={11} tone="accent">
          Connectors
        </MockItem>
      </ConnectorsNav>
      <ConnectorsContent>
        <ContentTitle>Connectors</ContentTitle>
        <AddConnectorButton>+ Add custom connector</AddConnectorButton>
        <ConnectorForm>
          <FieldLabel>Name</FieldLabel>
          <FieldValue>Bumper</FieldValue>
          <FieldLabel>Remote MCP server URL</FieldLabel>
          <FieldValue mono>{MCP_SERVER_URL}</FieldValue>
          <FieldLabel>OAuth client ID</FieldLabel>
          <FieldValue mono>{MCP_OAUTH_CLIENT_ID}</FieldValue>
        </ConnectorForm>
      </ConnectorsContent>
    </ConnectorsBody>
  </ConnectorsWindow>
);
